import moment from 'moment';
import { Children, isValidElement } from 'react';

export function getPercentageOfTwoNumbers(firstNumber: any, lastNumber: any) {
  // firstNumber indicates to Last month/year count
  // lastNumber indicates to latest month/year count
  let percentage = 0;
  if (lastNumber) {
    percentage = ((lastNumber - firstNumber) / firstNumber) * 100;
  }
  return percentage;
}

export function formatByByte(selectByte: string, kpiValue: any) {
  if (selectByte === 'PB') {
    return kpiValue;
  }

  if (selectByte === 'TB') {
    return kpiValue * 1024;
  }

  return kpiValue * 1024 * 1024;
}

export function formatNumberWithCommas(number: any, isDecimal: boolean = false) {
  if (number != null && !Number.isNaN(number)) {
    if (isDecimal) {
      const formattedNumber = Number(number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      if (formattedNumber === "-0.00") {
        return "0.00";
      }
      return formattedNumber;
    }
    else
      return Number(number).toLocaleString('en-US');
  }

  if (isDecimal) {
    return "0.00";
  } return "0";
}

export function getHoursList(timeGap: number) {
  return [
    '00:00',
    '00:30',
    '01:00',
    '01:30',
    '02:00',
    '02:30',
    '03:00',
    '03:30',
    '04:00',
    '04:30',
    '05:00',
    '05:30',
    '06:00',
    '06:30',
    '07:00',
    '07:30',
    '08:00',
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
    '19:30',
    '20:00',
    '20:30',
    '21:00',
    '21:30',
    '22:00',
    '22:30',
    '23:00',
    '23:30',
  ];
}

export function convertShortDateToActualDateFormat(selectedDate: any) {
  let formattedDate = '';
  if (selectedDate == '0M') {
    formattedDate = moment().format('YYYY-MM');
  } else if (selectedDate == '1M') {
    formattedDate = moment().subtract(1, 'months').format('YYYY-MM');
  } else if (selectedDate == '3M') {
    formattedDate = moment().subtract(3, 'months').format('YYYY-MM');
  } else if (selectedDate == '6M') {
    formattedDate = moment().subtract(6, 'months').format('YYYY-MM');
  } else if (selectedDate == '9M') {
    formattedDate = moment().subtract(9, 'months').format('YYYY-MM');
  } else if (selectedDate == '1Y') {
    formattedDate = moment().subtract(1, 'years').format('YYYY-MM');
  } else if (selectedDate == '2Y') {
    formattedDate = moment().subtract(2, 'years').format('YYYY-MM');
  } else {
    formattedDate = selectedDate;
  }
  return formattedDate;
}

export const formatIsoDate = (isoString: string) => {
  const date = new Date(isoString);

  const padTo2Digits = (num: any) => String(num).padStart(2, '0');

  const month = padTo2Digits(date.getUTCMonth() + 1); // getUTCMonth() returns months from 0-11
  const day = padTo2Digits(date.getUTCDate());
  const year = String(date.getUTCFullYear()).slice(-2); // Get last two digits of year

  return `${month}-${day}-${year}`;
};

const PLATFORM_MS_SET = new Set([1, 2, 3, 4, 7, 8, 9, 10, 11]);

const getDivisorByPlatform = (platformId: number): number => {
  if (PLATFORM_MS_SET.has(platformId)) {
    return 60000;
  }

  return 60;
};

const formatUnit = (value: number, singular: string, plural: string): string => {
  if (!value) return '';
  const label = value === 1 ? singular : plural;
  return `${value} ${label}`;
};

export const getHrsMinPlatformId = (totalMinutes: number, platformId: number, forceMs?: boolean): string => {
  const divisor = forceMs ? 60000 : getDivisorByPlatform(platformId);
  const minutesTotal = totalMinutes / divisor;

  const days = Math.floor(minutesTotal / (24 * 60));
  const hours = Math.floor((minutesTotal % (24 * 60)) / 60);
  const minutes = minutesTotal % 60;

  const parts: string[] = [];

  const dayPart = formatUnit(days, 'day', 'days');
  if (dayPart) parts.push(dayPart);

  const hourPart = formatUnit(hours, 'hr', 'hrs');
  if (hourPart) parts.push(hourPart);

  if (minutes > 0) {
    parts.push(`${minutes.toFixed(2)} min`);
  }

  if (!dayPart && !hourPart && minutes === 0) {
    return '--';
  } else {
    return parts.join(' ');
  }
};

const classNames = [
  "l1-s",
  "l1-t",
  "l1-f",
  "l1-fv",
  "l1-six",
  "l1-sv",
  "l1-eg",
  "l1-nn",
  "l1-tn",
  "l1-el",
  "l1-tw",
  "l1-th",
  "l1-ft",
  "l1-ff",
  "l1-st",
];

const levelNames = [
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "nineth",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
];

const traverse = (
  nodes: any[],
  idx: number,
  level: number,
  allDatas: any[]
) => {
  nodes.forEach((node) => {
    node.className = classNames[level] ?? `l1-${level}`;
    node.eleLevel = `l${idx}-${levelNames[level] ?? level + 2}`;
    node.priCyclic = node.depCyclic;

    allDatas.push(node);

    if (node.dependInfo?.length) {
      traverse(node.dependInfo, idx, level + 1, allDatas);
    }
  });
};

export const dependInfoTransformData = (data: any) => {
  console.log("DEPP", data);
  const allDatas: any[] = [];

  if (!Array.isArray(data)) {
    return allDatas;
  }

  data.forEach((d, idx) => {
    console.log("INFOOOO", d);
    traverse([d], idx, 0, allDatas);
  });

  return allDatas;
};

export const childInfoTransformData = (data: any) => {
  const allDatas: any[] = [];

  data.forEach((d: any, idx: number) => {
    d.className = 'l1-s';
    d.eleLevel = `l${idx}-second`;
    d.priCyclic = d.depCyclic;
    allDatas.push(d);

    d.dependInfo?.forEach((t: any) => {
      t.className = 'l1-t';
      t.eleLevel = `l${idx}-third`;
      t.priCyclic = t.depCyclic;
      allDatas.push(t);
    });
  });

  return allDatas;
}


export const convertDecimalToTime = (val: any) => {
  // Step 1: Calculate total hours, days, and remaining hours
  const totalHrs = Math.floor(val);
  const days = Math.floor(totalHrs / 24); // Full 24-hour periods
  const hrs = totalHrs % 24; // Remaining hours after extracting days

  // Step 2: Calculate minutes
  const hrsInDec = val - totalHrs;
  const min = Math.round(hrsInDec * 60);

  return { Days: days, Hours: hrs, Minutes: min };
};

export const formatNumber = (number: any) => {
  if (number != null && !Number.isNaN(number)) {
    return Number(number).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    return '0.00';
  }
};

export const formatNumberByUnits = (number: number) => {
  if (number >= 1000000) {
    return `${new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(number / 1000000)}MM`;
  }
  if (number >= 1000) {
    return `${formatNumberWithCommas((number / 1000).toFixed(2), true)}K`;
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
};

export function formatAbsNumber(number: any) {
  if (number != null && !Number.isNaN(number) && number != 0) {
    return Number(Math.abs(number)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    return '0';
  }
}

const plural = (value: number) => (value === 1 ? '' : 's');

const getElapsedTime = (date: Date) => {
  const now = new Date();
  const totalSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);

  return { now, totalSeconds, totalMinutes, hours };
};

const isYesterday = (date: Date, now: Date) => {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

export const timestampCal = (input: string | Date) => {
  const date = typeof input === 'string' ? new Date(input) : input;
  const { now, totalSeconds, totalMinutes, hours } = getElapsedTime(date);

  const formatTime = (d: Date) => {
    const hrs = d.getHours() % 12 || 12;
    const mins = d.getMinutes().toString().padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
    return `${hrs}:${mins} ${ampm}`;
  };

  const formatDate = (d: Date) =>
    `${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;

  if (totalSeconds <= 0) return 'Just Now';
  if (totalSeconds < 60) return `${totalSeconds} sec${plural(totalSeconds)} ago`;
  if (totalMinutes < 60) return `${totalMinutes} min${plural(totalMinutes)} ago`;
  if (hours < 24 && date.getDate() === now.getDate()) return `${hours} hr${plural(hours)} ago`;
  if (isYesterday(date, now)) return `Yesterday\n${formatTime(date)}`;

  return `${formatDate(date)}\n${formatTime(date)}`;
};

export const timestampCalWithFormat = (input: string | Date) => {
  const date = typeof input === 'string' ? new Date(input) : input;
  const { now, totalSeconds, totalMinutes, hours } = getElapsedTime(date);

  const formatFullDate = (d: Date) => {
    const weekday = d.toLocaleString('en-US', { weekday: 'short' });
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    const hrs = d.getHours() % 12 || 12;
    const mins = d.getMinutes().toString().padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
    return `${weekday}, ${day} ${month} ${year}, ${hrs}:${mins} ${ampm}`;
  };

  if (totalSeconds <= 0) return 'Just Now';
  if (totalSeconds < 60) return `${totalSeconds} sec${plural(totalSeconds)} ago`;
  if (totalMinutes < 60) return `${totalMinutes} min${plural(totalMinutes)} ago`;
  if (hours < 24 && date.getDate() === now.getDate()) return `${hours} hr${plural(hours)} ago`;
  if (isYesterday(date, now)) return `Yesterday, ${formatFullDate(date)}`;

  return formatFullDate(date);
};

export const getHrsMin = (value: number, unit: 'mins' | 'hrs' = 'mins') => {
  // convert input to minutes
  const totalMinutes = unit === 'hrs' ? Math.round(value * 60) : Math.round(value);

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  // Format the result
  let result = '';
  if (days > 0) result += `${days} Day${days > 1 ? 's' : ''} `;
  if (hours > 0) result += `${hours} Hr${hours > 1 ? 's' : ''} `;
  if (minutes > 0) result += `${minutes} Min${minutes > 1 ? 's' : ''}`;

  return result.trim() || '0 Mins';
};

export function nodeToText(node: any): string {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (isValidElement(node)) {
    const children = Children.toArray((node as any).props?.children || []);
    return children.map(nodeToText).join('').trim();
  }
  try {
    return String(node);
  } catch {
    return '';
  }
}

const extractBaseDate = (node: any): string[] => {
  const dates: string[] = [];

  if (node?.date) {
    dates.push(node.date);
  }

  if (node?.timestamp) {
    dates.push(node.timestamp.split('T')[0]);
  }

  return dates;
};

const extractRecords = (records: any[] = []): string[] =>
  records.flatMap((r: any) => {
    if (r?.timestamp) {
      return [r.timestamp.split('T')[0]];
    }

    if (r?.date) {
      return [r.date];
    }

    return [];
  });

const extractChildNode = (child: any): any => {
  const key = Object.keys(child ?? {})[0];
  return child?.[key];
};

const extractChildren = (children: any[] = []): string[] =>
  children.flatMap(child => {
    const val = extractChildNode(child);
    if (!val) return [];

    if (val.date || val.timestamp) {
      return extractBaseDate(val);
    }

    if (Array.isArray(val.records)) {
      return extractRecords(val.records);
    }

    return extractDatesFromNode(val);
  });

const extractMonthYear = (arr: any[], key: string): string[] =>
  arr.flatMap(item => {
    const value = key === 'month' ? (item.month ?? item) : item;
    return extractDatesFromNode(value);
  });

export function extractDatesFromNode(node: any): string[] {
  if (!node) return [];

  const dates = [
    ...extractBaseDate(node),
    ...extractRecords(node.records),
    ...extractChildren(node.children),
    ...extractMonthYear(node?.month?.children, 'month'),
    ...extractMonthYear(node?.year?.children, 'year'),
  ];

  return Array.from(new Set(dates)).filter(Boolean);
}

type NodeType = 'year' | 'month' | 'week' | 'day';

const normalize = (v: any) => (v == null ? '' : String(v).trim());

const deriveMonthFromId = (id?: string): string | null => {
  if (!id || typeof id !== 'string') return null;
  return /month-([\s\S]*?)-week/.exec(id)?.[1]?.trim() ?? null;
};

const isMatch = (node: any, type: NodeType, label: string) => {
  if (!node) return false;

  switch (type) {
    case 'year':
      return normalize(node.year_name) === normalize(label);

    case 'month':
      return normalize(node.month_name) === normalize(label);

    case 'week':
      return normalize(node.week_name) === normalize(label);

    case 'day':
      return (
        normalize(node.day_name) === normalize(label) ||
        normalize(node.date) === normalize(label)
      );
  }
};

function findDay(
  week: any,
  label: string,
  parentMonth?: string
) {
  if (!Array.isArray(week.children)) return null;

  for (const wrapper of week.children) {
    const key = Object.keys(wrapper)[0];
    const day = wrapper[key];

    if (!isMatch(day, 'day', label)) continue;

    return {
      kind: 'day',
      node: day,
      parentWeek: week.week_name,
      parentMonth
    };
  }

  return null;
}

function findWeek(
  month: any,
  type: NodeType,
  label: string
) {
  if (type === 'month' && isMatch(month, 'month', label)) {
    return {
      kind: 'month',
      node: month
    };
  }

  if (!Array.isArray(month.children)) return null;

  for (const wrapper of month.children) {
    const key = Object.keys(wrapper)[0];
    const week = wrapper[key];

    if (type === 'week' && isMatch(week, 'week', label)) {
      return {
        kind: 'week',
        node: week,
        parentMonth: month.month_name
      };
    }

    if (type === 'day') {
      const day = findDay(week, label, month.month_name);
      if (day) return day;
    }
  }

  return null;
}

function scanYear(
  year: any,
  type: NodeType,
  label: string
) {
  if (type === 'year' && isMatch(year, 'year', label)) {
    return {
      kind: 'year',
      node: year
    };
  }

  if (!Array.isArray(year.children)) return null;

  for (const wrapper of year.children) {
    const month = wrapper.month || wrapper;

    const result = findWeek(month, type, label);

    if (result) {
      return {
        ...result,
        parentYear: year.year_name
      };
    }
  }

  return null;
}

function scanRootWeek(
  week: any,
  type: NodeType,
  label: string
) {
  if (type === 'week' && isMatch(week, 'week', label)) {
    return {
      kind: 'week',
      node: week
    };
  }

  if (type === 'day') {
    return findDay(week, label);
  }

  return null;
}

function scanRootItem(
  item: any,
  type: NodeType,
  label: string
) {
  if (item.day && type === 'day' && isMatch(item.day, 'day', label)) {
    return {
      kind: 'day',
      node: item.day
    };
  }

  if (item.week) {
    const result = scanRootWeek(item.week, type, label);
    if (result) return result;
  }

  if (item.month) {
    const result = findWeek(item.month, type, label);
    if (result) return result;
  }

  if (item.year) {
    const result = scanYear(item.year, type, label);
    if (result) return result;
  }

  return null;
}

function searchYearMonths(
  year: any,
  monthName: string,
  type: NodeType,
  label: string
) {
  if (!year?.children) return null;

  for (const wrapper of year.children) {
    const month = wrapper.month || wrapper;

    if (normalize(month.month_name) !== normalize(monthName)) {
      continue;
    }

    const result = findWeek(month, type, label);

    if (result) {
      return {
        ...result,
        parentYear: year.year_name
      };
    }
  }

  return null;
}

function searchPreferredMonth(
  apiData: any[],
  monthName: string,
  type: NodeType,
  label: string
) {
  for (const item of apiData) {
    if (
      item.month &&
      normalize(item.month.month_name) === normalize(monthName)
    ) {
      const result = findWeek(item.month, type, label);
      if (result) return result;
    }

    if (!item.year) continue;

    const result = searchYearMonths(
      item.year,
      monthName,
      type,
      label
    );

    if (result) return result;
  }

  return null;
}

export function findSelectedNode(
  apiData: any,
  type: NodeType,
  label: string,
  rowId?: string
) {
  if (!apiData) return null;

  const preferredMonth = deriveMonthFromId(rowId);

  if (preferredMonth) {
    const result = searchPreferredMonth(
      apiData,
      preferredMonth,
      type,
      label
    );

    if (result) return result;
  }

  for (const item of apiData) {
    const result = scanRootItem(item, type, label);

    if (result) return result;
  }

  return null;
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 2,
});

// normalize dates for API: if start === end treat as same date
export const normalizeApiDates = (st?: string, ed?: string) => {
  if (!st) return { start: st, end: ed };
  const s = moment(st).format('YYYY-MM-DD');
  const e = ed ? moment(ed).format('YYYY-MM-DD') : s;
  return { start: s, end: e };
};

export function formatCompactNumber(value: number): string {
  if (value >= 2_000_000) {
    return Number.parseFloat((value / 1_000_000).toFixed(2)) + 'M';
  } else if (value >= 1_000) {
    return Number.parseFloat((value / 1_000).toFixed(2)) + 'K';
  } else {
    return Number.parseFloat(value.toFixed(2)).toString();
  }
}

export const formatHoursAsDays = (hours: number) => {
  const total = Math.round(hours); // if fractional hours not needed, round
  const days = Math.floor(total / 24);
  const remHours = total % 24;
  if (days > 0) {
    // show "Xd Yh" (e.g. "2d 5h")
    return `${days}d ${remHours}h`;
  }
  // small values show as "NN hrs" with compact formatting
  return `${formatCompactNumber(total)} hrs`;
};

// Helper to format SLA days
export function formatSlaDays(slaScheduleInfo: any[]) {
  if (!slaScheduleInfo || slaScheduleInfo.length === 0) return '';
  // Daily SLA
  if (slaScheduleInfo.some((info: any) => info.type === 0)) {
    return 'All Days';
  }
  // Weekly SLA
  // Flatten all slaData and collect week days
  const allSlaData = slaScheduleInfo.flatMap((info: any) => info.slaData || []);
  const allDaysArr = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  let dayArr = allSlaData
    .map((sla: any) => sla.SLA_Week_Days)
    .filter(Boolean)
    .flatMap((days: string) => days.split(',').map(d => d.trim()));
  // Deduplicate and sort days according to dayOrder
  dayArr = Array.from(new Set(dayArr)).filter(day => dayOrder.includes(day)).sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
  // Mon to Sun
  if (allDaysArr.every(day => dayArr.includes(day)) && dayArr.length === 7) {
    return 'All Days';
  }
  // Consecutive days
  const indices = dayArr.map(day => dayOrder.indexOf(day)).filter(idx => idx !== -1);
  if (indices.length > 1) {
    let isConsecutive = true;
    for (let i = 1; i < indices.length; i++) {
      if (indices[i] !== indices[i - 1] + 1) {
        isConsecutive = false;
        break;
      }
    }
    if (isConsecutive) {
      return `${dayOrder[indices[0]]} to ${dayOrder[indices.at(-1)!]}`;
    }
  }
  // Default: join days in sorted order
  return dayArr.join(', ');
}

export const AUTO_REFRESH_OPTIONS = [
  { label: "No Auto Refresh", value: 0 },
  { label: "5 Mins", value: 5 },
  { label: "10 Mins", value: 10 },
  { label: "20 Mins", value: 20 },
  { label: "30 Mins", value: 30 },
] as const;

export function formatDateRangeLabel(type: any): string {
  switch (type) {
    case 'today':
      return 'Today';
    case '7d':
      return 'Last 7 Days';
    case '24h':
      return 'Last 24 Hrs';
    case '30d':
      return 'Last 30 Days';
    case '90d':
      return 'Last 90 Days';
    default:
      return `Last ${type}`;
  }
}

export function FILE_LABELS(type: any): string {
  switch (type) {
    case 'Successful':
      return 'Successful_Jobs';
    case 'Aborted':
      return 'Aborted_Jobs';
    case 'InProgress':
      return 'In Progress_Jobs';
    case 'Delayed':
      return 'Delayed_Jobs';
    case 'Failed':
      return 'Failed_Jobs';
    case 'Scheduled':
      return 'Scheduled_Jobs';
    default:
      return 'Total_Jobs';
  }
};

export const TIMEZONE_MAP: Record<string, string> = {
  EST: 'America/New_York',
  CST: 'America/Chicago',
  PST: 'America/Los_Angeles',
  MST: 'America/Denver',
  UTC: 'UTC',
  GMT: 'Europe/London',
};

export function getRunningDays(slaScheduleInfo: any) {
  const runningDays = new Set();
  if (!slaScheduleInfo || !Array.isArray(slaScheduleInfo)) return runningDays;

  const isAllDaysSelected = slaScheduleInfo.some(info => info.type === 0);
  if (isAllDaysSelected) {
    for (let i = 0; i < 7; i++) runningDays.add(i);
    return runningDays;
  }

  const dayNameMap: any = {
    'sun': 0, 'sunday': 0,
    'mon': 1, 'monday': 1,
    'tue': 2, 'tuesday': 2,
    'wed': 3, 'wednesday': 3,
    'thu': 4, 'thursday': 4,
    'fri': 5, 'friday': 5,
    'sat': 6, 'saturday': 6
  };

  slaScheduleInfo.forEach(scheduleItem => {
    if (scheduleItem.slaData && Array.isArray(scheduleItem.slaData)) {
      scheduleItem.slaData.forEach((slaItem: any) => {
        if (slaItem.SLA_Week_Days) {
          const dayNames = slaItem.SLA_Week_Days.split(',').map((day: string) => day.trim().toLowerCase());
          dayNames.forEach((dayName: string) => {
            const dayIndex = dayNameMap[dayName];
            if (dayIndex !== undefined) runningDays.add(dayIndex);
          });
        }
      });
    }
  });

  return runningDays;
}

export const convertMsToHHMMSS = (ms: number | null | undefined): string => {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '00h 00m 00s';

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (num: number): string => String(num).padStart(2, '0');
  return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
};
export const formatExecutionTime = (row: any): string => {
  if (row.actualStartTime && row.actualEndTime) {
    const startSec = Math.floor(new Date(row.actualStartTime).getTime() / 1000) * 1000;
    const endSec = Math.floor(new Date(row.actualEndTime).getTime() / 1000) * 1000;
    const diffMs = endSec - startSec;
    if (Number.isFinite(diffMs) && diffMs >= 0) {
      return convertMsToHHMMSS(diffMs);
    }
  }
  return row.totalExecutionTime ? convertMsToHHMMSS(Number(row.totalExecutionTime)) : '--';
};

export const STANDARD_LABELS: Record<string, string> = {
  'today': 'Today',
  'yesterday': 'Yesterday',
  '5m': 'Last 5 Mins',
  '10m': 'Last 10 Mins',
  '15m': 'Last 15 Mins',
  '30m': 'Last 30 Mins',
  '1h': 'Last 1 Hour',
  '24h': 'Last 24 Hrs',
  '7d': 'Last 7 Days',
  '15d': 'Last 15 Days',
  '30d': 'Last 30 Days',
  '60d': 'Last 60 Days',
  '90d': 'Last 90 Days',
  '120d': 'Last 120 Days',
  '3m': 'Last 3 Months',
  '180d': 'Last 6 Months',
  '270d': 'Last 9 Months',
  'this_month': 'This Month',
  'last_month': 'Last Month',
  'last_3_months': 'Last 3 Months',
  'this_year': 'This Year',
  'last_year': 'Last Year',
  'last_2_years': 'Last 2 Years',
  'more_than_90_days': 'Older than 90 days',
  'previousyear': 'Previous Year',
  '1Y': 'Previous Year',
  'currentyear': 'Current Year',
  '0Y': 'Current Year',
};

export const getDateRangeLabel = (
  filterMonth: string,
  customMonth: string,
) => {
  if (customMonth) {
    return moment(customMonth, 'YYYY-MM').format('MMMM YYYY');
  }

  const dateLabels: Record<string, string> = {
    '0M': 'This Month',
    '1M': 'Last Month',
    '3M': 'Last 3 Months',
    '6M': 'Last 6 Months',
    '9M': 'Last 9 Months',
    '0Y': 'This Year',
    '1Y': 'Last Year',
    '2Y': 'Last 2 Years',
    '3Y': 'Last 3 Years',
  };

  return dateLabels[filterMonth] || '-';
};

export const getSectorLabel = (sectorName: string) => {
  if (!sectorName || sectorName.toLowerCase() === 'all') {
    return 'ALL SECTORS';
  }

  return sectorName.toUpperCase();
};

export const getAxisDisplayMax = (value: number) => {
  if (!value || value <= 0) return value;

  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;

  if (normalized <= 1.2) return 1.2 * magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 5) return 5 * magnitude;

  return 10 * magnitude;
};

export const scheduleTypeOptions = ['Daily', 'Weekly'];

export const deadlineTypeOptions = ['Clock', 'Interval'];

export const ruleOptions = ['Any Run', 'All Runs', 'Latest Run', 'Selected Run'];

export const slotOptions = ['Yes', 'No'];

export const dayOptions = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const NON_HISTORICAL_RANGE_TYPES = new Set([
  '5m',
  '10m',
  '15m',
  '30m',
  '1h',
  'today',
]);

export const RESTRICTED_ROLE_NAMES = ['PwC_View', 'PwC_Edit'];

export const RESTRICTED_ROUTES_FOR_PwC = [
  '/data-pipeline/programOpsDashboard',
  '/data-pipeline/home/executiveOps',
];